









































































































































































import Vue from "vue";
import Component from "vue-class-component";
import {Watch} from "vue-property-decorator";
import {useRentalStore} from "@/store/rental";
import type {RentalDevice} from "@/api/models/rental";
import SmbTextInput from "@/components/ui/SmbTextInput/SmbTextInput.vue";
import SmbTextArea from "@/components/ui/SmbTextArea/SmbTextArea.vue";
import SmbButton from "@/components/ui/SmbButton/SmbButton.vue";
import SmbSwitch from "@/components/ui/SmbSwitch/SmbSwitch.vue";
import SmbApiAlert from "@/components/ui/SmbAlert/SmbApiAlert.vue";

interface DeviceForm {
  name: string;
  description: string;
  pricePerDay: number;
  isAvailable: boolean;
}

@Component({
  components: {
    SmbTextInput,
    SmbTextArea,
    SmbButton,
    SmbSwitch,
    SmbApiAlert,
  },
  metaInfo() {
    return {
      title: (this as RentalDeviceEdit).isNew ? "Neues Leihgerät" : "Leihgerät bearbeiten",
    };
  },
})
export default class RentalDeviceEdit extends Vue {
  public isLoading = false;
  public isSaving = false;
  public isUploading = false;
  public deletingImageId: number | null = null;
  public errorMessage = "";

  public form: DeviceForm = {
    name: "",
    description: "",
    pricePerDay: 0,
    isAvailable: true,
  };

  public get isNew(): boolean {
    return this.$route.params.id === "new";
  }

  public get priceInput(): string {
    return this.form.pricePerDay.toString().replace(".", ",");
  }

  public set priceInput(value: string) {
    const normalized = value.replace(",", ".");
    const parsed = parseFloat(normalized);
    this.form.pricePerDay = isNaN(parsed) ? 0 : parsed;
  }

  public get deviceId(): number | null {
    if (this.isNew) return null;
    return parseInt(this.$route.params.id, 10);
  }

  public get device(): RentalDevice | null {
    return useRentalStore().currentDevice;
  }

  public get deviceImages(): { id: number; fileName: string; contentType: string }[] {
    return this.device && this.device.images ? this.device.images : [];
  }

  public isVideo(contentType: string): boolean {
    return contentType === "video/mp4";
  }

  public async mounted(): Promise<void> {
    if (!this.isNew && this.deviceId) {
      await this.loadDevice();
    }
  }

  @Watch("$route.params.id")
  public async onIdChanged(): Promise<void> {
    if (!this.isNew && this.deviceId) {
      await this.loadDevice();
    } else {
      this.resetForm();
    }
  }

  public async loadDevice(): Promise<void> {
    if (!this.deviceId) return;

    this.isLoading = true;
    try {
      const rentalStore = useRentalStore();
      const device = await rentalStore.fetchRentalDeviceById(this.deviceId, true);
      if (device) {
        this.form = {
          name: device.name,
          description: device.description || "",
          pricePerDay: device.pricePerDay,
          isAvailable: device.isAvailable,
        };
      }
    } catch (e) {
      console.error("Error loading device:", e);
      this.errorMessage = "Fehler beim Laden des Leihgeräts.";
    } finally {
      this.isLoading = false;
    }
  }

  public resetForm(): void {
    this.form = {
      name: "",
      description: "",
      pricePerDay: 0,
      isAvailable: true,
    };
    this.errorMessage = "";
  }

  public async saveDevice(): Promise<void> {
    this.errorMessage = "";
    this.isSaving = true;

    try {
      const rentalStore = useRentalStore();
      let result;

      if (this.isNew) {
        result = await rentalStore.createRentalDevice(
          this.form.name,
          this.form.description || null,
          this.form.pricePerDay
        );
        if (result.success) {
          await this.$router.push("/admin/rental/devices");
        }
      } else if (this.deviceId) {
        result = await rentalStore.updateRentalDevice(
          this.deviceId,
          this.form.name,
          this.form.description || null,
          this.form.pricePerDay,
          this.form.isAvailable
        );
      }

      if (result && !result.success) {
        this.errorMessage = result.message || "Fehler beim Speichern.";
      }
    } catch (e) {
      console.error("Error saving device:", e);
      this.errorMessage = "Fehler beim Speichern des Leihgeräts.";
    } finally {
      this.isSaving = false;
    }
  }

  public getImageUrl(imageId: number): string {
    const rentalStore = useRentalStore();
    return rentalStore.getImageUrl(imageId);
  }

  public triggerFileInput(): void {
    const input = this.$refs.fileInput as HTMLInputElement;
    if (input) {
      input.click();
    }
  }

  public async handleFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0 || !this.deviceId) return;

    const file = input.files[0];
    this.isUploading = true;

    try {
      const rentalStore = useRentalStore();
      const result = await rentalStore.uploadDeviceImage(this.deviceId, file);
      if (!result.success) {
        alert(result.message || "Fehler beim Hochladen.");
      }
    } catch (e) {
      console.error("Error uploading image:", e);
      alert("Fehler beim Hochladen des Bildes.");
    } finally {
      this.isUploading = false;
      input.value = "";
    }
  }

  public async deleteImage(imageId: number): Promise<void> {
    if (!this.deviceId) return;
    if (!confirm("Möchten Sie dieses Bild wirklich löschen?")) return;

    this.deletingImageId = imageId;
    try {
      const rentalStore = useRentalStore();
      const result = await rentalStore.deleteDeviceImage(imageId, this.deviceId);
      if (!result.success) {
        alert(result.message || "Fehler beim Löschen.");
      }
    } catch (e) {
      console.error("Error deleting image:", e);
      alert("Fehler beim Löschen des Bildes.");
    } finally {
      this.deletingImageId = null;
    }
  }
}
